/**
 * iter-231-S — Vendor-facing Razorpay Route Linked Account card.
 *
 * Mounts inside StoreProfile.jsx (Bank Details area). Shows the vendor:
 *   - Their Linked Account id + status (when present)
 *   - Preflight verdict (which profile fields are still missing)
 *   - Last creation attempt error (if any)
 *   - A "Create / Retry" button — enabled ONLY when current_state is
 *     `blocked_missing_fields` or `last_attempt_failed` (per user spec).
 *     `already_created` hides the button entirely; `ready_never_attempted`
 *     shows a soft "We'll activate this shortly" message because the
 *     admin backfill cron handles that state.
 *
 * Mirrors the admin RazorpayRouteDiagnoseCard but scoped to the
 * authenticated vendor's own data (no vendor_id prop — backend reads
 * `current_user`).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
  PlayCircle,
  RefreshCw,
  Wallet,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const STATE_INFO = {
  already_created: {
    label: 'Linked Account Active',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  ready_never_attempted: {
    label: 'Ready · Activation Pending',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  last_attempt_failed: {
    label: 'Last Attempt Failed',
    color: 'bg-red-100 text-red-800 border-red-200',
  },
  blocked_missing_fields: {
    label: 'Action Required',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
  },
};

const VendorRazorpayRouteCard = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('/vendor/razorpay/setup-status', { method: 'GET' });
      setData(res || null);
    } catch (err) {
      toast({
        title: 'Could not load payout setup',
        description: err?.message || 'Try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [apiCall, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    setCreating(true);
    setCreateResult(null);
    try {
      const res = await apiCall('/vendor/razorpay/create', { method: 'POST' });
      setCreateResult({ ok: !!res?.ok, body: res });
      if (res?.ok) {
        toast({
          title: 'Linked Account created',
          description: res.account_id
            ? `Razorpay ID: ${res.account_id}`
            : 'Activation in progress.',
        });
      } else {
        toast({
          title: 'Create failed',
          description:
            res?.error || res?.detail?.error || 'Razorpay rejected the request.',
          variant: 'destructive',
        });
      }
      await load();
    } catch (err) {
      let body = err?.response?.detail || err?.detail || null;
      try {
        if (typeof body === 'string') body = JSON.parse(body);
      } catch {
        /* keep raw */
      }
      setCreateResult({
        ok: false,
        body: body || { error: err?.message || 'create_failed' },
      });
      toast({
        title: 'Create failed',
        description:
          body?.error || body?.message || err?.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  if (loading && !data) {
    return (
      <Card data-testid="vendor-razorpay-route-card-loading">
        <CardContent className="py-6 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading payout setup…
        </CardContent>
      </Card>
    );
  }

  if (!data || data.not_applicable) return null;

  const state = data.current_state || 'ready_never_attempted';
  const stateInfo = STATE_INFO[state] || STATE_INFO.ready_never_attempted;
  const laId = data.linked_account?.id;
  const laStatus = (data.linked_account?.status || '').toLowerCase();
  const canTrigger = !!data.can_vendor_trigger;
  const isCreated = state === 'already_created';
  const missingSteps = data.next_steps || [];

  return (
    <Card className="bg-white" data-testid="vendor-razorpay-route-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-5 w-5" />
              Razorpay Payout Account
              <Badge
                variant="outline"
                className={stateInfo.color}
                data-testid="vendor-razorpay-state-badge"
              >
                {stateInfo.label}
              </Badge>
            </CardTitle>
            <CardDescription>
              Razorpay Route splits each order&apos;s vendor share directly to
              your bank. This account must be active to receive payouts.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="vendor-razorpay-refresh"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
            {canTrigger && (
              <Button
                size="sm"
                onClick={handleCreate}
                disabled={creating}
                data-testid="vendor-razorpay-create-btn"
              >
                {creating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <PlayCircle className="h-4 w-4 mr-2" />
                )}
                {state === 'last_attempt_failed'
                  ? 'Retry Create'
                  : 'Create Linked Account'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Active account confirmation */}
        {isCreated && laId && (
          <Alert className="bg-emerald-50 border-emerald-200">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <AlertTitle className="text-emerald-900">
              Payouts active
            </AlertTitle>
            <AlertDescription className="text-emerald-800">
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <code
                  className="text-xs bg-white px-2 py-1 rounded border"
                  data-testid="vendor-razorpay-account-id"
                >
                  {laId}
                </code>
                {laStatus && (
                  <span className="text-xs text-emerald-700">
                    Razorpay status: {laStatus}
                  </span>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Soft pending state (admin will auto-create) */}
        {state === 'ready_never_attempted' && (
          <Alert className="bg-blue-50 border-blue-200">
            <Loader2 className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-900">
              Awaiting activation
            </AlertTitle>
            <AlertDescription className="text-blue-800 text-sm">
              Your profile is complete. We&apos;ll create your Razorpay payout
              account within the next sync cycle (usually a few minutes).
              Check back soon — you&apos;ll see your account ID here once
              activated.
            </AlertDescription>
          </Alert>
        )}

        {/* Missing-field prompts (deep-link to edit pages) */}
        {state === 'blocked_missing_fields' && missingSteps.length > 0 && (
          <Alert className="bg-amber-50 border-amber-300">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-900">
              Complete these to enable payouts
            </AlertTitle>
            <AlertDescription>
              <ul
                className="mt-2 space-y-1.5"
                data-testid="vendor-razorpay-missing-steps"
              >
                {missingSteps.map((step) => (
                  <li key={step.field}>
                    <Link
                      to={step.edit_path}
                      className="inline-flex items-center gap-1 text-sm text-amber-900 hover:underline font-medium"
                      data-testid={`vendor-razorpay-step-${step.field}`}
                    >
                      {step.label}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </li>
                ))}
              </ul>
              {data.has_bank === false && (
                <p className="text-xs text-amber-700 mt-3">
                  Also add your bank account in{' '}
                  <Link
                    to="/vendor/compliance"
                    className="underline font-medium"
                    data-testid="vendor-razorpay-bank-link"
                  >
                    Compliance & KYC
                  </Link>
                  {' '}so payouts can credit your account.
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Last-attempt error (most recent Razorpay rejection) */}
        {data.last_attempt_error && state === 'last_attempt_failed' && (
          <Alert variant="destructive" data-testid="vendor-razorpay-last-error">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Previous attempt was rejected</AlertTitle>
            <AlertDescription>
              <p className="font-mono text-xs break-words">
                {typeof data.last_attempt_error === 'string'
                  ? data.last_attempt_error
                  : (data.last_attempt_error?.description
                      || data.last_attempt_error?.error
                      || data.last_attempt_error?.code
                      || JSON.stringify(data.last_attempt_error))}
              </p>
              {data.last_attempt_at && (
                <p className="text-xs mt-1 opacity-75">
                  at: {data.last_attempt_at}
                </p>
              )}
              <p className="text-xs mt-2">
                Update the missing details below, then press{' '}
                <strong>Retry Create</strong>.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Preflight snapshot — collapsible diagnostic block */}
        <div className="rounded border bg-slate-50 p-3">
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            className="text-xs font-medium text-gray-700 inline-flex items-center gap-1"
            data-testid="vendor-razorpay-toggle-diagnostic"
          >
            {showRaw ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
            {showRaw ? 'Hide' : 'Show'} diagnostic details
          </button>
          {showRaw && (
            <div
              className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-700"
              data-testid="vendor-razorpay-diagnostic-grid"
            >
              <div>PAN on file: {data.has_pan ? 'Yes' : 'No'}</div>
              <div>GSTIN on file: {data.has_gst ? 'Yes' : 'No'}</div>
              <div>Bank on file: {data.has_bank ? 'Yes' : 'No'}</div>
              <div>
                Business type:{' '}
                <code>{data.derived_business_type || 'individual'}</code>
              </div>
            </div>
          )}
        </div>

        {/* Latest create-attempt result (this session) */}
        {createResult && (
          <Alert
            variant={createResult.ok ? 'default' : 'destructive'}
            data-testid="vendor-razorpay-create-result"
          >
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {createResult.ok ? 'Razorpay accepted' : 'Razorpay rejected'}
            </AlertTitle>
            <AlertDescription>
              <pre className="text-xs bg-white text-gray-800 p-2 rounded overflow-auto max-h-48">
                {JSON.stringify(createResult.body, null, 2)}
              </pre>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default VendorRazorpayRouteCard;
